import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import client, { setUnauthorizedHandler } from '../api/client';
import { invalidateWelcomeSessionCache } from '../utils/welcomeSessionCache';
import { subscribeCacheRefreshed } from '../utils/cacheEvents';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [customers, setCustomers] = useState([]);
  const [uiPreferences, setUiPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  const applyMeResponse = useCallback((data) => {
    if (data?.authenticated) {
      setAuthenticated(true);
      setUser(data.user);
      setPermissions(data.permissions || {});
      setCustomers(data.customers || []);
      setUiPreferences(data.uiPreferences || {});
    } else {
      setAuthenticated(false);
      setUser(null);
      setPermissions({});
      setCustomers([]);
      setUiPreferences({});
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get('/api/auth/me');
      applyMeResponse(res.data);
    } catch {
      setAuthenticated(false);
      setUser(null);
      setPermissions({});
      setCustomers([]);
      setUiPreferences({});
    } finally {
      setLoading(false);
    }
  }, [applyMeResponse]);

  const updateUiPreferences = useCallback(async (patch) => {
    setUiPreferences(prev => ({ ...prev, ...patch }));
    try {
      const res = await client.patch('/api/auth/preferences', patch);
      if (res.data?.uiPreferences) {
        setUiPreferences(res.data.uiPreferences);
      }
    } catch {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthenticated(false);
      setUser(null);
      setPermissions({});
      setCustomers([]);
      setUiPreferences({});
    });
    refresh();
  }, [refresh]);

  // Re-fetch /api/auth/me when an admin mutates customers so assigned-user
  // lists (Home dashboard scope) update without a full page reload.
  useEffect(() => {
    return subscribeCacheRefreshed((detail) => {
      if (detail?.kind === 'customers') refresh();
    });
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await client.post('/api/auth/login', { email, password });
    applyMeResponse(res.data);
    return res.data;
  }, [applyMeResponse]);

  const logout = useCallback(async () => {
    try {
      await client.post('/api/auth/logout');
    } catch {}
    setAuthenticated(false);
    setUser(null);
    setPermissions({});
    setCustomers([]);
    setUiPreferences({});
    // Wipe per-page session caches so a different user signing in on the
    // same browser doesn't see the previous user's customers / metrics
    // before the next fetch lands.
    invalidateWelcomeSessionCache();
  }, []);

  const hasPermission = useCallback((pageKey, requireEdit = false) => {
    const entry = permissions[pageKey];
    if (!entry) return false;
    return requireEdit ? entry.canEdit : entry.canView;
  }, [permissions]);

  const value = useMemo(() => ({
    authenticated,
    loading,
    user,
    permissions,
    customers,
    uiPreferences,
    updateUiPreferences,
    hasPermission,
    login,
    logout,
    refresh,
  }), [authenticated, loading, user, permissions, customers, uiPreferences, updateUiPreferences, hasPermission, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
